import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import {
  STUDYGROUP_JOIN_POLICY_FREE,
  STUDYGROUP_JOIN_POLICY_INVITE_AND_REQUEST,
  STUDYGROUP_MEMBERSHIP_STATUS_INVITED,
  STUDYGROUP_MEMBERSHIP_STATUS_MEMBER,
  STUDYGROUP_MEMBERSHIP_STATUS_REQUESTING,
} from "@/constants";
import { studyGroupMembershipApi } from "@/lib/humhub-api/studyGroupMembership";
import { studyGroupsApi } from "@/lib/humhub-api/studyGroups";
import { completeMembershipTransition } from "@/lib/study-group/completeMembershipTransition";
import type { EventApiResponse } from "@/lib/humhub-api/types";
import { studyGroupKeys } from "@/lib/queryKeys/studyGroupKeys";

export type StudyGroupJoinResult = {
  success: boolean;
  action: "joined" | "applied" | "already_member" | "not_eligible";
  groupName: string;
  error?: string;
};

interface UseEventStudyGroupAutoJoinOptions {
  onComplete?: (result: StudyGroupJoinResult) => void;
}

interface UseEventStudyGroupAutoJoinReturn {
  handleAutoJoin: (
    eventData: EventApiResponse,
  ) => Promise<StudyGroupJoinResult>;
}

/**
 * イベント参加時のスタディグループ自動参加専用フック
 */
export const useEventStudyGroupAutoJoin = (
  options: UseEventStudyGroupAutoJoinOptions = {},
): UseEventStudyGroupAutoJoinReturn => {
  const { onComplete } = options;
  const queryClient = useQueryClient();

  const handleAutoJoin = useCallback(
    async (eventData: EventApiResponse): Promise<StudyGroupJoinResult> => {
      if (!eventData.container) {
        const result: StudyGroupJoinResult = {
          success: false,
          action: "not_eligible",
          groupName: "",
          error: "No container information",
        };
        onComplete?.(result);
        return result;
      }

      const {
        id: studyGroupId,
        name: groupName,
        join_policy: joinPolicy,
      } = eventData.container;

      try {
        // Step 1: 現在の参加状態を確認
        let membershipStatus = null;
        try {
          membershipStatus =
            await studyGroupMembershipApi.getStudyGroupYourMembership(
              studyGroupId,
            );
        } catch (error) {
          console.warn("Failed to get membership status:", error);
        }

        // Step 2: 既に参加済みの場合
        if (membershipStatus) {
          let action: StudyGroupJoinResult["action"] | undefined;

          switch (membershipStatus.status) {
            case STUDYGROUP_MEMBERSHIP_STATUS_INVITED:
              {
                const transitionResult = await completeMembershipTransition({
                  queryClient,
                  studyGroupId,
                  action: "force_join",
                  source: "auto_join_invited",
                });
                if (!transitionResult.lastVisitUpdated) {
                  console.warn(
                    "Last visit update failed after auto join from invitation",
                    { studyGroupId },
                  );
                }
              }
              action = "joined";
              break;
            case STUDYGROUP_MEMBERSHIP_STATUS_MEMBER:
              action = "already_member";
              break;
            case STUDYGROUP_MEMBERSHIP_STATUS_REQUESTING:
              action = "applied";
              break;
            default:
              console.warn(
                `Unknown membership status: ${membershipStatus.status}`,
              );
              break;
          }

          if (action) {
            const result: StudyGroupJoinResult = {
              success: true,
              action,
              groupName,
            };
            onComplete?.(result);
            return result;
          }
        }

        // Step 3: 参加ポリシーに応じた処理
        if (joinPolicy === STUDYGROUP_JOIN_POLICY_FREE) {
          // 自由参加 - 直接参加
          {
            const transitionResult = await completeMembershipTransition({
              queryClient,
              studyGroupId,
              action: "join",
              source: "auto_join_free",
            });
            if (!transitionResult.lastVisitUpdated) {
              console.warn("Last visit update failed after auto join", {
                studyGroupId,
              });
            }
          }

          const result: StudyGroupJoinResult = {
            success: true,
            action: "joined",
            groupName,
          };
          onComplete?.(result);
          return result;
        } else if (joinPolicy === STUDYGROUP_JOIN_POLICY_INVITE_AND_REQUEST) {
          // 招待制 - 参加申請
          await studyGroupsApi.applyMembership(studyGroupId);

          // 参加申請リスト関連のキャッシュを無効化（status="applicant"のクエリ）
          queryClient.invalidateQueries({
            queryKey: studyGroupKeys.myStudyGroupsRoot,
          });

          const result: StudyGroupJoinResult = {
            success: true,
            action: "applied",
            groupName,
          };
          onComplete?.(result);
          return result;
        }

        // その他のポリシー（招待のみ等）の場合
        const result: StudyGroupJoinResult = {
          success: true,
          action: "not_eligible",
          groupName,
        };
        onComplete?.(result);
        return result;
      } catch (error) {
        console.error("Study group auto-join failed:", error);

        const result: StudyGroupJoinResult = {
          success: false,
          action: "not_eligible",
          groupName,
          error: error instanceof Error ? error.message : "Unknown error",
        };
        onComplete?.(result);
        return result;
      }
    },
    [onComplete, queryClient],
  );

  return { handleAutoJoin };
};
